import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { useI18n } from '@/contexts/I18nContext';
import { useTechniques, getTechniqueName, getCategoryName } from '@/shared/hooks/useTechniques';

type TechniquesStepProps = {
  categoryIndex: number;
  setCategoryIndex: React.Dispatch<React.SetStateAction<number>>;
  selected: Record<string, string[]>;
  setSelected: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
};

export function TechniquesStep({ categoryIndex, setCategoryIndex, selected, setSelected }: TechniquesStepProps) {
  const { t, currentLanguage } = useI18n();
  const { data: techniqueCategories, isLoading } = useTechniques();
  
  const category = techniqueCategories?.[categoryIndex];

  // Estado local para seleção da categoria atual
  const [localSelected, setLocalSelected] = useState<string[]>(selected[category?.name || ''] || []);

  useEffect(() => {
    if (category?.name) {
      setLocalSelected(selected[category.name] || []);
    }
  }, [categoryIndex, category?.name]);

  // Efeito para sincronizar o estado local com o global
  useEffect(() => {
    if (category?.name) {
      setSelected((old) => ({ ...old, [category.name]: localSelected }));
    }
  }, [localSelected, category?.name, setSelected]);

  // Handler ajustado: agora só atualiza o estado local
  const handleSelect = (value: string) => {
    setLocalSelected((prev) => {
      if (prev.includes(value)) {
        return prev.filter((t) => t !== value);
      } else if (prev.length < 3) {
        return [...prev, value];
      } else {
        return [...prev.slice(1), value];
      }
    });
  };

  const handleNextCategory = () => {
    // Salvar técnicas da categoria atual
    const currentCategoryTechniques = selected[category.name] || [];
    const localSelected = [...currentCategoryTechniques];
    
    setSelected(prev => ({
      ...prev,
      [category.name]: localSelected
    }));

    // Avançar para próxima categoria ou finalizar
    if (categoryIndex < ((techniqueCategories as any[])?.length || 0) - 1) {
      setCategoryIndex(categoryIndex + 1);
    }
  };

  const handlePrevCategory = () => {
    if (categoryIndex > 0) {
      setCategoryIndex(categoryIndex - 1);
    }
  };

  // Verificar se a categoria atual tem 3 técnicas selecionadas
  const hasThreeTechniques = localSelected.length === 3;
  const isLastCategory = categoryIndex === ((techniqueCategories as any[])?.length || 0) - 1;

  return (
    <div className="text-black">
      <h2 className="text-lg font-semibold mb-2 font-sans">{t('onboarding.techniques.title')}</h2>
      <p className="mb-4 text-black font-sans">{t('onboarding.techniques.select_techniques')}</p>
      
      {/* Progress indicator */}
      <div className="mb-4">
        <div className="flex justify-between text-xs text-[#b0b0b0] mb-2">
          <span>{t('onboarding.techniques.category', { current: categoryIndex + 1, total: (techniqueCategories as any[])?.length || 0 })}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-sossiety-red h-2 rounded-full transition-all duration-300" 
            style={{ width: `${((categoryIndex + 1) / ((techniqueCategories as any[])?.length || 1)) * 100}%` }}
          ></div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading techniques...</div>
      ) : (
        <div className="mb-4">
          <div className="font-medium mb-1 text-black font-sans">{getCategoryName(category, currentLanguage)}</div>
        <div className="text-xs text-[#b0b0b0] mb-2 font-sans">{t('onboarding.techniques.max_selected', { max: localSelected.length })}</div>
        <div className="flex flex-wrap gap-2">
          {category?.techniques?.map((tech) => {
            const isSelected = localSelected.includes(tech.name);
            const isDisabled = !isSelected && localSelected.length >= 3;
            return (
              <button
                key={tech.id}
                type="button"
                onClick={() => handleSelect(tech.name)}
                className={
                  'rounded px-3 py-1 text-sm font-medium transition-colors duration-200 border font-sans ' +
                  (isSelected
                    ? 'bg-sossiety-red text-white border-sossiety-red'
                    : isDisabled
                    ? 'border-black text-[#b0b0b0] bg-white cursor-not-allowed'
                    : 'border-black text-black bg-white hover:bg-sossiety-red hover:text-white cursor-pointer') +
                  ' focus:outline-none focus:ring-0'
                }
                tabIndex={0}
                aria-pressed={isSelected}
                disabled={false}
              >
                {getTechniqueName(tech, currentLanguage)}
              </button>
            );
          })}
        </div>
        
        {/* Warning message if not 3 techniques selected */}
        {localSelected.length > 0 && localSelected.length < 3 && (
          <div className="text-xs text-orange-600 mt-2 font-sans">
            💡 Tip: You can select up to 3 techniques for better recommendations
          </div>
        )}
        </div>
      )}
    </div>
  );
} 